import React, { useState } from 'react';
import round from '../../../helpers/round';

import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EastIcon from '@mui/icons-material/East';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Link from '@mui/material/Link';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const PilerocksettleResult = ({ result }) => {
  const { Q, socketLength, socketDiameter, ec, em, ips } = result.inputs;
  const { canUserSeeCalculationSteps, drOverBr, ecOverEm, pileTipArea, settlementM, settlementMm } = result.outputs;

  const PileRockSettleInfoBox = () => {
    const [showCalculationDetails, setShowCalculationDetails] = useState(false);
    const isSmallScreen = useMediaQuery('(max-width:700px)');

    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Immediate Settlement of Pile Foundations in Rock (AASHTO, 2002)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            According to AASHTO (2002), the immediate settlement of a pile foundation socketed or bearing on rock depends on the
            deformation characteristics of both the pile and the rock mass. The method relates the elastic compression of the pile
            and the elastic deformation of the supporting rock to estimate the total immediate settlement under the applied load.
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              marginTop: '15px',
              marginBottom: '10px',
              lineHeight: 1.6,
              fontWeight: 600,
            }}
          >
            <math display="block">
              <msub>
                <mi>&rho;</mi>
                <mi>s</mi>
              </msub>
              <mo>=</mo>
              <mi>Q</mi>
              <mo>[</mo>
              <mfrac>
                <mrow>
                  <msub>
                    <mi>I</mi>
                    <mi>ps</mi>
                  </msub>
                </mrow>
                <mrow>
                  <msub>
                    <mi>B</mi>
                    <mi>r</mi>
                  </msub>
                  <msub>
                    <mi>E</mi>
                    <mi>m</mi>
                  </msub>
                </mrow>
              </mfrac>
              <mo>+</mo>
              <mfrac>
                <mrow>
                  <msub>
                    <mi>D</mi>
                    <mi>r</mi>
                  </msub>
                </mrow>
                <mrow>
                  <mi>
                    <mi>A</mi>
                  </mi>
                  <msub>
                    <mi>E</mi>
                    <mi>c</mi>
                  </msub>
                </mrow>
              </mfrac>
              <mo>]</mo>
            </math>
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowCalculationDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showCalculationDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  &rho;<sub>s</sub> (Settlement):{' '}
                </b>
                {settlementM ? settlementM.toFixed(3) : '—'} m
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showCalculationDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    By substituting all known parameters into the expression, the immediate settlement,&nbsp;
                    <i>
                      &rho;<sub>s</sub>
                    </i>
                    &nbsp;can be evaluated using the following equation:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>&rho;</mi>
                        <mi>s</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{round(Q, 2)}</mn>
                      <mo>[</mo>
                      <mfrac>
                        <mrow>
                          <mn>{round(ips, 2)}</mn>
                        </mrow>
                        <mrow>
                          <mn>{round(socketDiameter, 2)}</mn>
                          <mo>x</mo>
                          <mn>{round(em, 2)}</mn>
                        </mrow>
                      </mfrac>
                      <mo>+</mo>
                      <mfrac>
                        <mrow>
                          <mn>{round(socketLength, 2)}</mn>
                        </mrow>
                        <mrow>
                          <mn>{round(pileTipArea, 2)}</mn>
                          <mo>x</mo>
                          <mn>{round(ec, 2)}</mn>
                        </mrow>
                      </mfrac>
                      <mo>]</mo>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    And from here;
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>&rho;</mi>
                        <mi>s</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{round(settlementM, 5)}</mn>
                      <mi>m</mi>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Or,
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>&rho;</mi>
                        <mi>s</mi>
                      </msub>
                      <mo>=</mo>
                      <mn>{round(settlementMm, 2)}</mn>
                      <mi> mm</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };

  return (
    <section className="pilerocksettleOutput">
      <h2>Output</h2>
      <PileRockSettleInfoBox />
    </section>
  );
};

export default PilerocksettleResult;
